<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AlertTriangle, ArrowRight, AudioLines, BookOpenCheck, Check, Clock3, Eye, FlaskConical, HeartHandshake, Image, Layers3, LockKeyhole, ShieldCheck, Sparkles, Star, UsersRound } from '@lucide/vue'
import { ApiError, api, postJson } from '../api'
import { auth } from '../state'
import type { SurveyDetail } from '../types'

const route = useRoute()
const router = useRouter()
const survey = ref<SurveyDetail | null>(null)
const loading = ref(true)
const accepted = ref(false)
const password = ref('')
const error = ref('')
const reportOpen = ref(false)
const reportCategory = ref('misleading')
const reportDescription = ref('')
const reportMessage = ref('')
const userAge = computed(() => auth.user ? new Date().getFullYear() - auth.user.birthYear : null)
const ageBlocked = computed(() => survey.value && userAge.value !== null && userAge.value < survey.value.minAge)
const resultMode = computed(() => survey.value?.definition?.presentation?.resultMode ?? survey.value?.resultMode ?? 'profile')
const titleLengthClass = computed(() => ({
  'long-survey-title': (survey.value?.title.length ?? 0) > 14,
  'very-long-survey-title': (survey.value?.title.length ?? 0) > 26,
}))

async function load() {
  error.value = ''
  try {
    survey.value = await api(`/api/surveys/${route.params.slug}`)
  }
  catch (err) { error.value = err instanceof ApiError ? err.message : '暂时无法打开问卷。' }
  finally { loading.value = false }
}
onMounted(load)

async function unlock() {
  error.value = ''
  try { await postJson(`/api/surveys/${route.params.slug}/unlock`, { password: password.value }); await load() }
  catch (err) { error.value = err instanceof ApiError ? err.message : '无法解锁问卷。' }
}

async function start() {
  if (survey.value?.requireLogin && !auth.user) return router.push({ path: '/login', query: { redirect: `/surveys/${route.params.slug}/take` } })
  if (auth.user?.emailVerified === false) return router.push('/account')
  if (!accepted.value || ageBlocked.value) return
  await router.push(`/surveys/${route.params.slug}/take`)
}

async function reportSurvey() {
  if (!survey.value || reportDescription.value.trim().length < 10) return
  await postJson(`/api/surveys/${survey.value.id}/reports`, { category: reportCategory.value, description: reportDescription.value })
  reportDescription.value = ''
  reportMessage.value = '举报已提交，平台会按内容治理规则处理。'
}

const typeName: Record<string, string> = { psychology: '心理探索', research: '研究实验', general: '一般问卷', sensitive: '敏感专区' }
</script>

<template>
  <div v-if="loading" class="page-loading"><span class="loader-orb" /><p>正在准备这段旅程…</p></div>
  <div v-else-if="survey?.locked" class="locked-survey section-pad" :style="{ '--accent': survey.accent }">
    <section class="account-action-card"><LockKeyhole/><p class="eyebrow">PROTECTED SURVEY</p><h1>{{ survey.title }}</h1><p>创作者为这份问卷设置了访问密码。</p><label class="field-label"><span>问卷密码</span><input v-model="password" type="password" @keyup.enter="unlock"/></label><p v-if="error" class="form-error">{{ error }}</p><button class="button button-dark" @click="unlock">解锁问卷 <ArrowRight/></button></section>
  </div>
  <div v-else-if="survey && survey.definition" class="detail-page section-pad" :style="{ '--accent': survey.accent }">
    <div class="detail-backdrop"><span /><span /><span /></div>
    <section class="detail-main">
      <div class="detail-badge"><Sparkles :size="15" />{{ typeName[survey.type] }}</div>
      <div class="detail-result-mode" :class="resultMode"><Image v-if="resultMode === 'profile'"/><Layers3 v-else/><span><b>{{ resultMode === 'profile' ? '含 IP 角色结果' : '纯维度结果' }}</b><small>{{ resultMode === 'profile' ? '结果包含创作者自定义角色或形象' : '结果不包含角色形象，减少标签影响' }}</small></span></div>
      <p class="eyebrow">{{ survey.category }} · VERSION {{ survey.version }}</p>
      <h1 :class="titleLengthClass">{{ survey.title }}</h1>
      <p class="detail-description">{{ survey.description }}</p>
      <div :class="['detail-community-note', survey.communityZone]">
        <HeartHandshake v-if="survey.communityZone === 'heartlight'"/><BookOpenCheck v-else/>
        <div><b>{{ survey.communityZone === 'research' ? '量域馆 · 科研问卷' : '心光馆 · 轻探索' }}</b><p>{{ survey.communityIntro || survey.description }}</p></div>
        <span v-if="survey.communityZone === 'research'" class="detail-impact"><Star/>影响 {{ survey.impactLevel || 1 }}/5</span>
      </div>
      <div class="detail-meta">
        <span><Clock3 />约 {{ survey.estimatedMinutes }} 分钟</span>
        <span><UsersRound />{{ survey.responseCount }} 人已完成</span>
        <span><Eye/>{{ survey.viewCount || 0 }} 次浏览</span>
        <span><ShieldCheck />{{ survey.communityZone === 'research' ? (survey.selfAssessmentVerified ? '材料初筛 + 站长审核' : '站长审核入馆') : '站长人工审核入馆' }}</span>
      </div>
      <div class="detail-intro"><span class="quote-mark">“</span><p>{{ survey.definition.intro }}</p></div>
    </section>

    <aside class="start-panel">
      <div class="mini-orbit"><span>{{ survey.title.slice(0, 1) }}</span></div>
      <h2>开始前，请了解</h2>
      <ul>
        <li><Check />共 {{ survey.definition.questions.length }} 道题，可随时退出</li>
        <li><AudioLines />支持实时动效与声音反馈</li>
        <li v-if="resultMode === 'profile'"><Image />完成后将获得创作者设置的 IP 角色结果</li>
        <li v-else><Layers3 />完成后仅展示维度分数，不显示 IP 角色</li>
        <li><LockKeyhole />答案仅用于本次结果与匿名统计</li>
        <li v-if="survey.definition.experiment"><FlaskConical />可能随机进入不同界面版本</li>
      </ul>
      <div class="consent-box">
        <p>{{ survey.definition.consent }}</p>
        <label><input v-model="accepted" type="checkbox" /><span class="custom-check"><Check /></span><span>我已阅读并自愿开始</span></label>
      </div>
      <p v-if="ageBlocked" class="inline-error">该问卷面向 {{ survey.minAge }} 岁及以上用户，你当前无法参与。</p>
      <p v-if="auth.user?.emailVerified === false" class="inline-error">请先在账号中心完成邮箱验证。</p>
      <button class="button button-dark full-button" :disabled="!accepted || Boolean(ageBlocked) || auth.user?.emailVerified === false" @click="start">进入问卷 <ArrowRight /></button>
      <small>内容、图片授权与量表声明由创作者负责；平台保留必要审核与处置权。本结果不构成医学诊断或治疗建议。</small>
      <div class="survey-report-box"><button type="button" @click="reportOpen = !reportOpen"><AlertTriangle/>举报此问卷或其 IP 内容</button><div v-if="reportOpen"><select v-model="reportCategory"><option value="misleading">误导性内容</option><option value="harmful">不当或有害内容</option><option value="copyright">版权/肖像问题</option><option value="privacy">隐私问题</option><option value="other">其他</option></select><textarea v-model="reportDescription" rows="3" placeholder="请描述具体问题（至少 10 字），不要在此填写个人敏感信息。"/><button class="button button-small" :disabled="reportDescription.trim().length < 10" @click="reportSurvey">提交举报</button><p v-if="reportMessage">{{ reportMessage }}</p></div></div>
    </aside>

  </div>
  <div v-else class="empty-state page-wrap"><h1>这份问卷暂时不可用</h1><p v-if="error">{{ error }}</p><RouterLink class="button" to="/explore">返回发现页</RouterLink></div>
</template>
